import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  Row,
  Spinner,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import Paginations from "components/Common/Paginations"
import isPermissionEnabled from "common/isPermissionEnabled"
import WebsiteBuilder from "modules/website-builder/pages/WebsiteBuilder"
import QrWebsiteBuilder from "modules/website-builder/pages/QrWebsiteBuilder"
import {
  WEBSITE_BUILDER_TYPES,
  normalizeWebsiteBuilderType,
  getWebsiteBuilderTypeLabelKey,
} from "modules/website-builder/constants"
import { saGetAllTenants, saGetTenantDetails } from "store/actions/superAdminAction"

const TENANTS_PER_PAGE = 12

const normalizeTenantStatusLabel = tenant => {
  if (tenant?.statusLabel) return tenant.statusLabel
  if (tenant?.status === 1) return "active"
  if (tenant?.status === 0) return "inactive"
  if (tenant?.status === 3) return "suspended"
  if (tenant?.status === 4) return "pending"
  return String(tenant?.status || "").toLowerCase() || "inactive"
}

const SuperAdminWebsiteBuilder = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const initialTenantId = searchParams.get("tenantId") || ""
  const websiteType = normalizeWebsiteBuilderType(searchParams.get("websiteType"))
  const pageNumber = parseInt(searchParams.get("page"), 10) || 1
  const builderTitleKey = getWebsiteBuilderTypeLabelKey(websiteType)

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const {
    tenantsLoading,
    tenants,
    tenantsTotalCount,
    tenantsTotalPages,
  } = useSelector(state => state.SuperAdmin)

  const [searchInput, setSearchInput] = useState("")
  const [searchKeyword, setSearchKeyword] = useState("")
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [resolvingTenant, setResolvingTenant] = useState(false)
  const tenantResolveAttempted = useRef(false)

  document.title = t("Website Builder | Super Admin")

  const canView = isPermissionEnabled(
    userRole,
    109,
    "sa_website_builder_1",
    accountType,
    accountScope
  )

  const canUpdate = isPermissionEnabled(
    userRole,
    109,
    "sa_website_builder_3",
    accountType,
    accountScope
  )

  const loadTenants = useCallback(
    (page = 1, keyword = "") => {
      dispatch(
        saGetAllTenants({
          page,
          limit: TENANTS_PER_PAGE,
          keyword: keyword || undefined,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    tenantResolveAttempted.current = false
  }, [initialTenantId])

  useEffect(() => {
    if (!canView || selectedTenant || initialTenantId) return
    loadTenants(pageNumber, searchKeyword)
  }, [canView, initialTenantId, loadTenants, pageNumber, searchKeyword, selectedTenant])

  useEffect(() => {
    if (!initialTenantId || selectedTenant) return

    const match = Array.isArray(tenants)
      ? tenants.find(tenant => String(tenant._id) === String(initialTenantId))
      : null

    if (match) {
      setSelectedTenant(match)
      return
    }

    if (tenantResolveAttempted.current) return
    tenantResolveAttempted.current = true

    let cancelled = false
    setResolvingTenant(true)

    dispatch(saGetTenantDetails({ id: initialTenantId }))
      .then(response => {
        if (cancelled) return
        const tenant = response?.payload?.data
        if (response?.payload?.status && tenant?._id) {
          setSelectedTenant(tenant)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setResolvingTenant(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, initialTenantId, selectedTenant, tenants])

  const handleSearch = event => {
    event.preventDefault()
    const nextKeyword = searchInput.trim()
    setSearchKeyword(nextKeyword)
    navigate("/superadmin/website-builder?page=1")
  }

  const handleResetSearch = () => {
    setSearchInput("")
    setSearchKeyword("")
    navigate("/superadmin/website-builder?page=1")
  }

  const handlePageChange = page => {
    navigate(`/superadmin/website-builder?page=${page}`)
  }

  const handleSelectTenant = (tenant, nextWebsiteType = WEBSITE_BUILDER_TYPES.STOREFRONT) => {
    setSelectedTenant(tenant)
    navigate(
      `/superadmin/website-builder?tenantId=${tenant._id}&websiteType=${nextWebsiteType}`,
      { replace: true }
    )
  }

  const handleChangeTenant = () => {
    setSelectedTenant(null)
    navigate("/superadmin/website-builder?page=1", { replace: true })
  }

  if (!canView) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Super Admin")} breadcrumbItem={t("Website Builder")} />
          <Card>
            <CardBody>{t("You do not have permission to access Website Builder.")}</CardBody>
          </Card>
        </Container>
      </div>
    )
  }

  if (resolvingTenant && initialTenantId && !selectedTenant) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t("Super Admin")} breadcrumbItem={t("Website Builder")} />
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    )
  }

  if (selectedTenant) {
    return (
      <>
        <div className="page-content pb-0">
          <Container fluid>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div>
                <Badge color="primary" className="me-2">
                  {t("Tenant")}: {selectedTenant.name || selectedTenant.slug}
                </Badge>
                <Badge color="info" className="me-2">
                  {t(builderTitleKey)}
                </Badge>
                {selectedTenant.domain ? (
                  <span className="text-muted small">{selectedTenant.domain}</span>
                ) : null}
              </div>
              <Button color="light" size="sm" onClick={handleChangeTenant}>
                {t("Change tenant")}
              </Button>
            </div>
          </Container>
        </div>
        {websiteType === WEBSITE_BUILDER_TYPES.QR ? (
          <QrWebsiteBuilder
            tenantId={String(selectedTenant._id)}
            tenantLabel={selectedTenant.name || selectedTenant.slug || selectedTenant._id}
            breadcrumbRoot="Super Admin"
            readOnly={!canUpdate}
          />
        ) : (
          <WebsiteBuilder
            tenantId={String(selectedTenant._id)}
            tenantLabel={selectedTenant.name || selectedTenant.slug || selectedTenant._id}
            breadcrumbRoot="Super Admin"
            readOnly={!canUpdate}
            websiteType={websiteType}
          />
        )}
      </>
    )
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title={t("Super Admin")} breadcrumbItem={t("Website Builder")} />

        <Card>
          <CardBody>
            <h4 className="mb-2">{t("Choose a tenant")}</h4>
            <p className="text-muted">
              {t(
                "Select the tenant whose storefront website you want to customize. Saved designs are stored per tenant."
              )}
            </p>

            <Row className="mb-3 g-2">
              <Col md="8" lg="6">
                <form onSubmit={handleSearch}>
                  <InputGroup>
                    <Input
                      type="search"
                      value={searchInput}
                      placeholder={t("Search by name, slug, domain, or tenant ID")}
                      onChange={event => setSearchInput(event.target.value)}
                    />
                    <Button color="primary" type="submit">
                      <i className="bx bx-search" />
                    </Button>
                  </InputGroup>
                </form>
              </Col>
              <Col md="4" lg="2">
                <Button
                  color="light"
                  className="w-100 d-flex align-items-center justify-content-center"
                  onClick={handleResetSearch}
                  disabled={tenantsLoading && !searchKeyword && !searchInput}
                >
                  <i className="mdi mdi-refresh me-1" style={{ fontSize: 18 }} />
                  {t("Reset")}
                </Button>
              </Col>
            </Row>

            {tenantsLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : tenants.length === 0 ? (
              <p className="text-muted mb-0">{t("No tenants found.")}</p>
            ) : (
              <>
                <Row>
                  {tenants.map(tenant => {
                    const statusLabel = normalizeTenantStatusLabel(tenant)
                    return (
                      <Col key={tenant._id} md="6" lg="4" className="mb-3">
                        <Card
                          className="border h-100"
                          role="button"
                          onClick={() => handleSelectTenant(tenant, WEBSITE_BUILDER_TYPES.STOREFRONT)}
                          style={{ cursor: "pointer" }}
                        >
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <h5 className="mb-0">{tenant.name || tenant.slug}</h5>
                              <Badge color={statusLabel === "active" ? "success" : "secondary"}>
                                {t(statusLabel)}
                              </Badge>
                            </div>
                            {tenant.slug ? (
                              <p className="text-muted small mb-1">{tenant.slug}</p>
                            ) : null}
                            {tenant.domain ? (
                              <p className="text-muted small mb-1">{tenant.domain}</p>
                            ) : null}
                            <p className="text-muted small mb-3 font-monospace">
                              {String(tenant._id)}
                            </p>
                            <div className="d-flex flex-column gap-2">
                              <Button
                                color="primary"
                                size="sm"
                                block
                                onClick={event => {
                                  event.stopPropagation()
                                  handleSelectTenant(tenant, WEBSITE_BUILDER_TYPES.STOREFRONT)
                                }}
                              >
                                {t("Build website")}
                              </Button>
                              <Button
                                color="primary"
                                outline
                                size="sm"
                                block
                                onClick={event => {
                                  event.stopPropagation()
                                  handleSelectTenant(tenant, WEBSITE_BUILDER_TYPES.QR)
                                }}
                              >
                                {t("Build QR website")}
                              </Button>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>

                {tenants.length > 0 ? (
                  <Paginations
                    currentPage={pageNumber}
                    totalPages={tenantsTotalPages || 1}
                    onPageChange={handlePageChange}
                    totalCount={tenantsTotalCount}
                  />
                ) : null}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default SuperAdminWebsiteBuilder
